// src/pages/seo/components/PagesTab.tsx

import React from 'react';
import { FieldWithAI } from './FieldWithAI';
import { InfoPopover } from './InfoPopover';
import { toast } from '@/hooks/use-toast';

type PageRow = {
  slug: string;
  meta_title?: string;
  meta_description?: string;
  h1?: string;
  canonical?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  keywords?: string[];
  internal_links?: string[];
  index?: boolean;
  follow?: boolean;
  // History is the field causing the circle, so we explicitly remove it.
  history?: any[]; 
};

function charCount(s?: string) {
  return (s || '').length;
}
function cls(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(' ');
}
function adminHeaders(): HeadersInit {
  try {
    const isAuthed = typeof window !== 'undefined' && localStorage.getItem('fca_admin_authed_v1') === '1';
    const headers = isAuthed ? { 'x-admin-ok': '1' } : {};
    console.log(`[PAGES TAB AUTH] State: ${isAuthed ? 'Authorized' : 'Unauthorized'} | Headers:`, headers);
    return headers;
  } catch (e) { 
    console.error('[PAGES TAB AUTH] Error reading localStorage:', e);
    return {}; 
  }
}

export function PagesTab({
  data,
  save,
  ai,
  onDirtyChange,
}: {
  data: any;
  save: (patch: any) => void;
  ai: (k: string, c: any) => Promise<string>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const pages: PageRow[] = data?.pages || [];

  if (!pages.length) {
    return (
      <div className="space-y-3">
        <div className="p-3 text-sm text-gray-600">
          No pages found yet. We’re trying to import them from your sitemap automatically…
        </div>
        <button
          className="border rounded px-3 py-2 text-sm"
          onClick={async () => {
            try {
              const r = await fetch('/api/seo', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...adminHeaders() },
                body: JSON.stringify({ action: 'pages.syncNow' }), 
              });
              const j = await r.json();
              if (j?.ok) {
                await fetch('/api/seo?action=getBundle', {
                  credentials: 'include',
                  headers: adminHeaders(),
                });
                alert(`Imported ${j.result?.added || 0} new page(s).`);
              } else {
                alert(j?.error || 'Import failed');
              }
            } catch (e: any) {
              alert(e?.message || 'Import failed');
            }
          }}
        >
          Import from sitemap now
        </button>
        <p className="text-xs text-gray-500">
          Tip: You can also navigate away and back to Pages after importing to see the updates.
        </p>
      </div>
    );
  }

  // Sidebar search
  const [q, setQ] = React.useState('');
  const filtered = !q
    ? pages
    : pages.filter((p) => p.slug.toLowerCase().includes(q.toLowerCase()));

  // pick first available page
  const [active, setActive] = React.useState<string>(pages[0]?.slug || '/');

  // find the active page
  const page =
    (pages.find((p) => p.slug === active) as PageRow) || ({ slug: active } as PageRow);

  // local edit state
  const [title, setTitle] = React.useState<string>(page.meta_title || '');
  const [desc, setDesc] = React.useState<string>(page.meta_description || '');
  const [h1, setH1] = React.useState<string>(page.h1 || '');
  const [canonical, setCanonical] = React.useState<string>(page.canonical || '');
  const [ogTitle, setOgTitle] = React.useState<string>(page.og_title || '');
  const [ogDesc, setOgDesc] = React.useState<string>(page.og_description || '');
  const [ogImage, setOgImage] = React.useState<string>(page.og_image || '');
  const [keywords, setKeywords] = React.useState<string>((page.keywords || []).join(', '));
  const [internalLinks, setInternalLinks] = React.useState<string>(
    (page.internal_links || []).join('\n')
  );
  const [isDirty, setIsDirty] = React.useState(false);
  
  const [canonicalInfoOpen, setCanonicalInfoOpen] = React.useState(false);
  const [ogImageInfoOpen, setOgImageInfoOpen] = React.useState(false);
  const [keywordsInfoOpen, setKeywordsInfoOpen] = React.useState(false);
  const [internalLinksInfoOpen, setInternalLinksInfoOpen] = React.useState(false);

  // sync when switching pages
  React.useEffect(() => {
    setTitle(page.meta_title || '');
    setDesc(page.meta_description || '');
    setH1(page.h1 || '');
    setCanonical(page.canonical || '');
    setOgTitle(page.og_title || '');
    setOgDesc(page.og_description || '');
    setOgImage(page.og_image || '');
    setKeywords((page.keywords || []).join(', '));
    setInternalLinks((page.internal_links || []).join('\n'));
    setIsDirty(false);
  }, [
    active,
    page.meta_title,
    page.meta_description,
    page.h1,
    page.canonical,
    page.og_title,
    page.og_description,
    page.og_image,
    page.keywords,
    page.internal_links,
  ]);

  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // duplicate-title detection
  const dupTitleSlugs = React.useMemo(() => {
    const map = new Map<string, string[]>();
    pages.forEach((p) => {
      const t = (p.meta_title || '').trim();
      if (!t) return;
      map.set(t, [...(map.get(t) || []), p.slug]);
    });
    return map;
  }, [pages]);
  const isDupTitle =
    title.trim() && (dupTitleSlugs.get(title.trim()) || []).filter((s) => s !== active).length > 0;

  // AI autofill helpers (apply result directly to fields)
  async function aiFill(field: 'title' | 'desc' | 'h1' | 'ogTitle' | 'ogDesc') {
    const ctx = { pagePath: active, h1, brand: data?.settings?.brand };
    switch (field) {
      case 'title':
        setTitle(await ai(`page:${active}.meta_title`, ctx));
        break;
      case 'desc':
        setDesc(await ai(`page:${active}.meta_description`, ctx));
        break;
      case 'h1':
        setH1(await ai(`page:${active}.h1`, ctx));
        break;
      case 'ogTitle':
        setOgTitle(await ai(`page:${active}.og_title`, ctx));
        break;
      case 'ogDesc':
        setOgDesc(await ai(`page:${active}.og_description`, ctx));
        break;
    }
  }
  async function aiFillAll() {
    await Promise.all([aiFill('title'), aiFill('desc'), aiFill('h1'), aiFill('ogTitle'), aiFill('ogDesc')]);
  }

  // Add a new page (slug prompt)
  function addPage() {
    const slug = prompt('Enter new page slug (e.g., /about):', '/new-page');
    if (!slug) return;
    const s = slug.startsWith('/') ? slug : `/${slug}`;
    if (pages.some((p) => p.slug === s)) {
      alert('A page with this slug already exists.');
      return;
    }
    // REMOVED historyDelta for simplicity
    save({
      page: { slug: s, index: true, follow: true },
    });
    setActive(s);
  }

  // Persist current form
  function savePage() {
    const next = {
      slug: active,
      meta_title: title || null,
      meta_description: desc || null,
      h1: h1 || null,
      canonical: canonical || null,
      og_title: ogTitle || null,
      og_description: ogDesc || null,
      og_image: ogImage || null,
      keywords: keywords
        ? keywords
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      internal_links: internalLinks
        ? internalLinks
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };
    console.log('[PAGES TAB SAVE] Sending page payload:', { page: next }); 
    // REMOVED historyDelta: let the API handle history lookups
    save({
      page: next,
    });
    setIsDirty(false);
    toast({
      title: "Page saved",
      description: `SEO settings for ${active} have been saved successfully.`,
    });
  }

  if (!pages.length) {
    return (
      <div className="space-y-3">
        <div className="p-3 text-sm text-gray-600">
          No pages found yet. We’re trying to import them from your sitemap automatically…
        </div>
        <button
          className="border rounded px-3 py-2 text-sm"
          onClick={async () => {
            try {
              const r = await fetch('/api/seo', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...adminHeaders() },
                body: JSON.stringify({ action: 'pages.seedFromSitemap' }),
              });
              const j = await r.json();
              if (j?.ok) window.location.reload();
              else alert(j?.error || 'Import failed');
            } catch (e: any) {
              alert(e?.message || 'Import failed');
            }
          }}
        >
          Import from sitemap now
        </button>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-5">
      {/* Sidebar */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            className="w-full border rounded p-2 text-sm"
            placeholder="Search pages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="border rounded px-3 text-sm" onClick={addPage}>
            + Add
          </button>
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-auto pr-1">
          {filtered.map((p) => (
            <button
              key={p.slug}
              className={cls(
                'w-full text-left border rounded p-2 hover:bg-gray-50 transition',
                active === p.slug && 'bg-gray-100'
              )}
              onClick={() => setActive(p.slug)}
              title={p.slug}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate">{p.slug}</span>
                {!p.index || !p.follow ? (
                  <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-100 text-yellow-900">
                    hidden
                  </span>
                ) : null}
              </span>
              <div className="text-[11px] text-gray-500 truncate">
                {p.meta_title || <em>(no title)</em>}
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="text-xs text-gray-500">No matches.</div>}
        </div>

        <div className="pt-2">
          <button
            className="border rounded px-3 py-2 text-sm w-full"
            onClick={async () => {
              try {
                const r = await fetch('/api/seo', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json', ...adminHeaders() },
                  body: JSON.stringify({ action: 'pages.seedFromSitemap' }),
                });
                const j = await r.json();
                if (j?.ok) window.location.reload();
                else alert(j?.error || 'Import failed');
              } catch (e: any) {
                alert(e?.message || 'Import failed');
              }
            }}
          >
            Import from sitemap
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="space-y-5">
        {/* Status/controls row */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="text-sm">
            <span className="font-medium">Editing:</span> <code>{active}</code>
          </div>

          {/* visibility toggles */}
          <div className="ml-auto flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={page.index !== false}
                onChange={(e) =>
                  save({
                    page: { slug: active, index: e.target.checked },
                    // REMOVED historyDelta
                  })
                }
              />
              index
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={page.follow !== false}
                onChange={(e) =>
                  save({
                    page: { slug: active, follow: e.target.checked },
                    // REMOVED historyDelta
                  })
                }
              />
              follow
            </label>
          </div>
        </div>

        {/* Title */}
        <FieldWithAI
          label={`Meta Title (${charCount(title)} chars${isDupTitle ? '; DUPLICATE' : ''})`}
          fieldKey={`page:${active}.meta_title`}
          value={title}
          onChange={(v) => { setTitle(v); setIsDirty(true); }}
          aiSuggest={(k) => ai(k, { pagePath: active, h1, brand: data?.settings?.brand })}
          context={{}}
          onUndo={() => {}}
          previous={page.meta_title}
        />
        {isDupTitle && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 -mt-3">
            Warning: Another page already uses this exact title. Consider making it unique.
          </div>
        )}

        {/* Description */}
        <FieldWithAI
          label={`Meta Description (${charCount(desc)} chars)`}
          fieldKey={`page:${active}.meta_description`}
          value={desc}
          onChange={(v) => { setDesc(v); setIsDirty(true); }}
          aiSuggest={(k) => ai(k, { pagePath: active, h1, brand: data?.settings?.brand })}
          context={{}}
          onUndo={() => {}}
          previous={page.meta_description}
        />

        {/* H1 */}
        <FieldWithAI
          label="H1"
          fieldKey={`page:${active}.h1`}
          value={h1}
          onChange={(v) => { setH1(v); setIsDirty(true); }}
          aiSuggest={(k) => ai(k, { pagePath: active, brand: data?.settings?.brand })}
          context={{}}
          onUndo={() => {}}
          previous={page.h1}
        />

        {/* Canonical */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <label className="font-medium">Canonical URL</label>
            <button className="text-xs underline" onClick={()=>setCanonicalInfoOpen(v=>!v)}>Info</button>
          </div>
          {canonicalInfoOpen && (
            <div className="border rounded bg-white shadow p-3 my-2">
              <InfoPopover fieldKey="page:/.canonical" />
            </div>
          )}
          <input
            className="w-full border rounded p-2"
            placeholder="https://www.example.com/your-page"
            value={canonical}
            onChange={(e) => { setCanonical(e.target.value); setIsDirty(true); }}
          />
        </div>

        {/* Open Graph */}
        <FieldWithAI
          label="OG Title"
          fieldKey={`page:${active}.og_title`}
          value={ogTitle}
          onChange={(v) => { setOgTitle(v); setIsDirty(true); }}
          aiSuggest={(k) => ai(k, { pagePath: active, h1, brand: data?.settings?.brand })}
          context={{}}
          onUndo={() => {}}
          previous={page.og_title}
        />
        <FieldWithAI
          label="OG Description"
          fieldKey={`page:${active}.og_description`}
          value={ogDesc}
          onChange={(v) => { setOgDesc(v); setIsDirty(true); }}
          aiSuggest={(k) => ai(k, { pagePath: active, h1, brand: data?.settings?.brand })}
          context={{}}
          onUndo={() => {}}
          previous={page.og_description}
        />
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <label className="font-medium">OG Image URL</label>
            <button className="text-xs underline" onClick={()=>setOgImageInfoOpen(v=>!v)}>Info</button>
          </div>
          {ogImageInfoOpen && (
            <div className="border rounded bg-white shadow p-3 my-2">
              <InfoPopover fieldKey="page:/.og_image" />
            </div>
          )}
          <input
            className="w-full border rounded p-2"
            placeholder="https://…/image.jpg"
            value={ogImage}
            onChange={(e) => { setOgImage(e.target.value); setIsDirty(true); }}
          />
        </div>

        {/* Keywords */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <label className="font-medium">Keywords (comma-separated)</label>
            <button className="text-xs underline" onClick={()=>setKeywordsInfoOpen(v=>!v)}>Info</button>
          </div>
          {keywordsInfoOpen && (
            <div className="border rounded bg-white shadow p-3 my-2">
              <InfoPopover fieldKey="page:/.keywords" />
            </div>
          )}
          <textarea
            className="w-full border rounded p-2"
            rows={2}
            placeholder="custom cakes, wedding cakes, chagrin falls"
            value={keywords}
            onChange={(e) => { setKeywords(e.target.value); setIsDirty(true); }}
          />
        </div>

        {/* Internal links */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <label className="font-medium">Internal Links (one per line)</label>
            <button className="text-xs underline" onClick={()=>setInternalLinksInfoOpen(v=>!v)}>Info</button>
          </div>
          {internalLinksInfoOpen && (
            <div className="border rounded bg-white shadow p-3 my-2">
              <InfoPopover fieldKey="page:/.internal_links" />
            </div>
          )}
          <textarea
            className="w-full border rounded p-2"
            rows={3}
            placeholder="/gallery\n/pricing\n/contact"
            value={internalLinks}
            onChange={(e) => { setInternalLinks(e.target.value); setIsDirty(true); }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button 
            className="rounded px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
            onClick={savePage}
            disabled={!isDirty}
          >
            {isDirty ? 'Save Page' : 'Saved'}
          </button>
          <button
            className="rounded px-4 py-2 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={aiFillAll}
            title="Autofill Title, Description, H1, OG Title & OG Description with AI"
          >
            Autofill with AI
          </button>
        </div>
      </div>
    </div>
  );
}